import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Download, ShieldCheck, AlertTriangle, Check, BellRing } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const ynm = (v) => (v === true ? "Yes" : v === false ? "No" : v || "");

// Column definitions — mirrors the resident intake form
const COLUMNS = [
  { key: "name", label: "Full Name", get: (g) => g.profile?.name || g.name || "" },
  { key: "preferred_name", label: "Preferred Name", get: (g) => g.profile?.preferred_name || "" },
  { key: "date_of_birth", label: "Date of Birth", get: (g) => fmtDate(g.profile?.date_of_birth) },
  { key: "nationality", label: "Nationality", get: (g) => g.profile?.nationality || "" },
  { key: "email", label: "Email", get: (g) => g.email || "" },
  { key: "phone", label: "Phone", get: (g) => g.profile?.phone || "" },
  { key: "emergency_name", label: "Emergency Contact", get: (g) => g.profile?.emergency_contact_name || g.profile?.emergency_contact || "" },
  { key: "emergency_phone", label: "Emergency Phone", get: (g) => g.profile?.emergency_contact_phone || "" },
  { key: "home_address", label: "Address", get: (g) => g.profile?.home_address || "" },
  { key: "arrival_date", label: "Arrival", get: (g) => fmtDate(g.arrival?.arrival_date) },
  { key: "departure_date", label: "Departure", get: (g) => fmtDate(g.arrival?.departure_date) },
  { key: "room_name", label: "Room", get: (g) => g.arrival?.room_name || "" },
  { key: "guests_during_stay", label: "Guests During Stay", get: (g) => ynm(g.arrival?.guests_during_stay) },
  { key: "pickup", label: "Airport Pickup", get: (g) => ynm(g.arrival?.pickup_requested) },
  { key: "accommodation_help", label: "Accommodation Help", get: (g) => ynm(g.arrival?.accommodation_help) },
  { key: "stay_transport", label: "Own Transport", get: (g) => ({ car: "Car", scooter: "Scooter", none: "No vehicle" }[g.arrival?.stay_transport] || "") },
  { key: "food_delivery_ack", label: "Food Delivery", get: (g) => (g.arrival?.stay_transport === "none" ? ynm(g.arrival?.food_delivery_ack) : "") },
  { key: "passport_number", label: "Passport/ID #", get: (g) => g.intake?.passport_number || "" },
  { key: "visa_info", label: "Visa", get: (g) => g.intake?.visa_info || "" },
  { key: "id_photo_front", label: "ID Front", get: (g) => g.intake?.id_photo_front || "", isDoc: true },
  { key: "id_photo_back", label: "ID Back", get: (g) => g.intake?.id_photo_back || "", isDoc: true },
  { key: "stay_reason", label: "Stay Reason", get: (g) => (g.intake?.stay_reason || "").replace(/_/g, " ") },
  { key: "stay_reason_detail", label: "Stay Reason Detail", get: (g) => g.intake?.stay_reason_detail || "" },
  { key: "stay_departure_date", label: "Expected Departure", get: (g) => fmtDate(g.intake?.stay_departure_date) },
  { key: "why_savi", label: "Why Savi", get: (g) => g.intake?.why_savi || "" },
  { key: "coliving_experience", label: "Coliving Experience", get: (g) => (g.intake?.coliving_experience || "").replace("_", " ") },
  { key: "daily_routine", label: "Daily Routine", get: (g) => g.intake?.daily_routine || "" },
  { key: "remote_work", label: "Remote Work", get: (g) => ynm(g.intake?.remote_work) },
  { key: "working_hours", label: "Working Hours", get: (g) => g.intake?.working_hours || "" },
  { key: "work_needs", label: "Work Needs", get: (g) => g.intake?.work_needs || "" },
  { key: "workshops", label: "Workshops", get: (g) => g.intake?.workshops || "" },
  { key: "passions", label: "Passions", get: (g) => g.intake?.passions || "" },
  { key: "volunteering", label: "Volunteering", get: (g) => ynm(g.intake?.volunteering) },
  { key: "music_preference", label: "Music", get: (g) => (g.intake?.music_preference === "tunes" ? "Loves tunes" : g.intake?.music_preference === "quiet" ? "Prefers quiet" : "") },
  { key: "social_events", label: "Social Events", get: (g) => ynm(g.intake?.social_events) },
  { key: "social_media_ok", label: "Photo Consent", get: (g) => ynm(g.profile?.social_media_ok) },
  { key: "rules_accepted", label: "Rules Agreed", get: (g) => ynm(g.onboarding?.rules_accepted) },
  { key: "onboarded", label: "Onboarding Done", get: (g) => ynm(g.onboarding?.final_confirmed) },
];

const csvEscape = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Items Steve needs to chase before check-in
const getMissing = (g) => {
  const m = [];
  if (!g.intake?.passport_number) m.push("Passport #");
  if (!g.intake?.id_photo_front) m.push("ID photo");
  if (!g.is_volunteer && !g.intake?.stay_reason) m.push("Reason for stay");
  if (!g.profile?.date_of_birth) m.push("DOB");
  if (!g.profile?.emergency_contact_phone && !g.profile?.emergency_contact) m.push("Emergency contact");
  return m;
};

const MissingBadge = ({ missing }) => {
  if (missing.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium">
        <Check className="w-3 h-3" /> Complete
      </span>
    );
  }
  const shown = missing.slice(0, 2).join(", ");
  const extra = missing.length > 2 ? ` +${missing.length - 2}` : "";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-medium"
      title={`Missing: ${missing.join(", ")}`}
    >
      <AlertTriangle className="w-3 h-3" /> {shown}{extra}
    </span>
  );
};

const RegistryTab = ({ guests, headers }) => {
  const FILE_BASE = API.replace(/\/api$/, "");
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [nudged, setNudged] = useState({}); // guestId -> ISO timestamp (local optimistic state)
  const [nudging, setNudging] = useState(null);

  const nudge = async (g) => {
    setNudging(g.id);
    try {
      const res = await axios.post(`${API}/admin/guests/${g.id}/nudge`, {}, { headers });
      setNudged((prev) => ({ ...prev, [g.id]: res.data.last_nudged_at }));
      toast.success(`Nudged ${g.name} 👋 They'll see it in their app.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't send nudge");
    } finally {
      setNudging(null);
    }
  };

  const lastNudge = (g) => nudged[g.id] || g.last_nudged_at;

  const allRows = useMemo(
    () => guests.filter((g) => g.role !== "admin"),
    [guests]
  );
  const missingCount = useMemo(() => allRows.filter((g) => getMissing(g).length > 0).length, [allRows]);
  const rows = showMissingOnly ? allRows.filter((g) => getMissing(g).length > 0) : allRows;

  const exportCsv = () => {
    const header = ["Missing Info", ...COLUMNS.map((c) => c.label)].map(csvEscape).join(",");
    const lines = rows.map((g) =>
      [
        csvEscape(getMissing(g).join("; ")),
        ...COLUMNS.map((c) => {
          const v = c.get(g);
          return csvEscape(c.isDoc && v ? `${FILE_BASE}${v}` : v);
        }),
      ].join(",")
    );
    const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `savi-guest-registry-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="registry-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg sm:text-xl text-[#3D4035]">Guest Registry ({rows.length})</h2>
          <p className="text-xs text-[#3D4035]/50 flex items-center gap-1 mt-0.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Contains personal data & ID documents — handle per GDPR, don't share.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {missingCount > 0 && (
            <Button
              onClick={() => setShowMissingOnly(!showMissingOnly)}
              size="sm"
              variant={showMissingOnly ? "default" : "outline"}
              className={`rounded-full text-xs ${
                showMissingOnly
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "border-red-200 text-red-600 hover:bg-red-50"
              }`}
              data-testid="registry-missing-filter-btn"
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              {missingCount} missing info
            </Button>
          )}
          <Button
            onClick={exportCsv}
            size="sm"
            className="bg-[#3D4035] hover:bg-[#2E2A26] text-white rounded-full"
            data-testid="registry-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="text-xs whitespace-nowrap" data-testid="registry-table">
            <thead className="sticky top-0 z-20">
              <tr className="bg-[#F4F1DE]">
                {COLUMNS.map((c, i) => (
                  <th
                    key={c.key}
                    className={`text-left font-semibold text-[#3D4035] px-3 py-2.5 border-b border-stone-200 ${
                      i === 0 ? "sticky left-0 z-10 bg-[#F4F1DE]" : ""
                    }`}
                  >
                    {c.label}
                  </th>
                )).reduce((acc, th, i) => {
                  acc.push(th);
                  if (i === 0) {
                    acc.push(
                      <th key="missing" className="text-left font-semibold text-[#3D4035] px-3 py-2.5 border-b border-stone-200">
                        Missing Info
                      </th>
                    );
                  }
                  return acc;
                }, [])}
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => {
                const missing = getMissing(g);
                return (
                <tr
                  key={g.id}
                  className={`border-b border-stone-50 ${missing.length > 0 ? "bg-red-50/40 hover:bg-red-50/70" : "hover:bg-[#FBF7F1]"}`}
                  data-testid={`registry-row-${g.email}`}
                >
                  {COLUMNS.map((c, i) => {
                    const v = c.get(g);
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 text-[#3D4035]/80 align-top max-w-[260px] truncate ${
                          i === 0 ? `sticky left-0 z-10 font-medium text-[#3D4035] ${missing.length > 0 ? "bg-[#FDF4F3]" : "bg-white"}` : ""
                        }`}
                        title={typeof v === "string" ? v : undefined}
                      >
                        {c.isDoc && v ? (
                          <a
                            href={`${FILE_BASE}${v}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#E07A5F] underline"
                          >
                            View doc
                          </a>
                        ) : (
                          v || <span className="text-stone-300">—</span>
                        )}
                      </td>
                    );
                  }).reduce((acc, td, i) => {
                    acc.push(td);
                    if (i === 0) {
                      acc.push(
                        <td key="missing" className="px-3 py-2 align-top whitespace-nowrap" data-testid={`registry-missing-${g.email}`}>
                          <div className="flex items-center gap-2">
                            <MissingBadge missing={missing} />
                            {missing.length > 0 && (
                              <button
                                onClick={() => nudge(g)}
                                disabled={nudging === g.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#E07A5F]/40 text-[#E07A5F] text-[11px] font-medium hover:bg-[#E07A5F] hover:text-white transition-colors disabled:opacity-50"
                                title={lastNudge(g) ? `Last nudged ${fmtDate(lastNudge(g))}` : "Send an in-app reminder to finish check-in info"}
                                data-testid={`registry-nudge-${g.email}`}
                              >
                                <BellRing className="w-3 h-3" />
                                {nudging === g.id ? "..." : lastNudge(g) ? "Nudge again" : "Nudge"}
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    }
                    return acc;
                  }, [])}
                </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="px-4 py-8 text-center text-[#3D4035]/40">
                    No guests yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RegistryTab;
